import {
  ArrowLeft,
  Briefcase,
  CalendarClock,
  Coins,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Trash2,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import {
  useClient,
  useClientSummary,
  useCurrentUser,
  useDeleteClient,
  useJobsForClient,
  useOrganizations,
} from "@/api/hooks";
import type { ClientResponse, ClientSummaryResponse } from "@/api/types";
import { ClientForm } from "@/components/ClientForm";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { formatTime, shortId } from "@/lib/format";

export function ClientDetailPage() {
  const { clientId = "" } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const me = useCurrentUser();
  const isPencilOutAdmin = me.data?.role === "pencilout_admin";
  const clientQuery = useClient(clientId);
  const summaryQuery = useClientSummary(clientId);
  const jobsQuery = useJobsForClient(clientId);
  const orgsQuery = useOrganizations();
  const deleteClient = useDeleteClient();

  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  if (clientQuery.isLoading) {
    return <p className="text-sm text-graphite-soft">Loading client…</p>;
  }
  if (clientQuery.error && !clientQuery.data) {
    return (
      <p className="text-sm text-red-pencil">
        {clientQuery.error instanceof ApiError
          ? clientQuery.error.detail
          : "Could not load client."}
      </p>
    );
  }
  const client = clientQuery.data;
  if (!client) return null;

  return (
    <div className="space-y-6">
      <Link
        to="/clients"
        className="inline-flex items-center gap-1 text-sm text-graphite-soft hover:text-graphite"
      >
        <ArrowLeft className="h-3 w-3" aria-hidden />
        All clients
      </Link>

      {editing ? (
        <ClientForm
          mode="edit"
          client={client}
          isPencilOutAdmin={isPencilOutAdmin}
          orgs={orgsQuery.data ?? []}
          onClose={() => setEditing(false)}
        />
      ) : (
        <ClientHeader
          client={client}
          onEdit={() => setEditing(true)}
          onDelete={() => setConfirmingDelete(true)}
        />
      )}

      <ClientFactsGrid client={client} />

      {summaryQuery.data && <ClientSummaryCard summary={summaryQuery.data} />}

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-mono text-lg font-bold tracking-tight">Jobs</h2>
          <span className="label-mono">{jobsQuery.data?.length ?? 0} total</span>
        </div>

        {jobsQuery.isLoading && !jobsQuery.data && (
          <p className="text-sm text-graphite-soft">Loading jobs…</p>
        )}

        {jobsQuery.data && jobsQuery.data.length === 0 ? (
          <div className="rounded-lg border border-rule bg-paper-warm p-8 text-center">
            <Briefcase className="mx-auto h-6 w-6 text-graphite-faint" aria-hidden />
            <p className="mt-3 text-sm text-graphite-soft">
              No jobs yet for this client.{" "}
              <Link to="/" className="font-medium text-graphite hover:underline">
                Create one from the Jobs page
              </Link>{" "}
              and pick this client from the dropdown.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {jobsQuery.data?.map((job) => (
              <li
                key={job.id}
                className="rounded-lg border border-rule bg-paper-warm transition hover:bg-pencil-yellow-soft"
              >
                <Link
                  to={`/jobs/${job.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-graphite">{job.title}</span>
                      <StatusBadge status={job.status} />
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 font-mono text-xs text-graphite-soft tabular-nums">
                      <span>by {job.creator_display_name}</span>
                      <span>
                        {job.walkthrough_count} walkthrough{job.walkthrough_count === 1 ? "" : "s"}
                      </span>
                      <span>{formatTime(job.updated_at)}</span>
                      <span className="text-graphite-faint">{shortId(job.id)}</span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {client.notes && (
        <section className="space-y-3">
          <h2 className="font-mono text-lg font-bold tracking-tight">Notes</h2>
          <article className="rounded-lg border border-rule bg-paper-warm p-5 text-sm whitespace-pre-wrap text-graphite">
            {client.notes}
          </article>
        </section>
      )}

      {confirmingDelete && (
        <ConfirmDialog
          title={`Delete ${client.name}?`}
          body={
            <>
              Removes this client. Jobs already linked to them stay; their{" "}
              <span className="font-mono">client_id</span> field clears to NULL. Quotes already
              drafted are unaffected.
              {deleteClient.error && (
                <span className="mt-2 block text-red-pencil">
                  {deleteClient.error instanceof ApiError
                    ? deleteClient.error.detail
                    : "Could not delete."}
                </span>
              )}
            </>
          }
          confirmLabel="Delete client"
          variant="danger"
          busy={deleteClient.isPending}
          onConfirm={() =>
            deleteClient.mutate(client.id, {
              onSuccess: () => navigate("/clients"),
              onError: () => setConfirmingDelete(false),
            })
          }
          onCancel={() => {
            setConfirmingDelete(false);
            deleteClient.reset();
          }}
        />
      )}
    </div>
  );
}

function ClientHeader({
  client,
  onEdit,
  onDelete,
}: {
  client: ClientResponse;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="font-mono text-2xl font-bold tracking-tight">{client.name}</h1>
        <p className="mt-1 font-mono text-xs text-graphite-soft">
          {client.organization_name && <>{client.organization_name} · </>}
          {shortId(client.id)} · added {formatTime(client.created_at)}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-3 py-2 font-mono text-sm font-semibold text-graphite transition hover:bg-pencil-yellow-soft"
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden /> Edit client
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-3 py-2 font-mono text-sm font-semibold text-graphite transition hover:bg-red-pencil/10 hover:text-red-pencil"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden /> Delete
        </button>
      </div>
    </header>
  );
}

function ClientFactsGrid({ client }: { client: ClientResponse }) {
  const addressLines: string[] = [];
  if (client.address_line1) addressLines.push(client.address_line1);
  if (client.address_line2) addressLines.push(client.address_line2);
  const cityRegionZip = [
    [client.city, client.region].filter(Boolean).join(", "),
    client.postal_code,
  ]
    .filter(Boolean)
    .join(" ");
  if (cityRegionZip) addressLines.push(cityRegionZip);
  if (client.country) addressLines.push(client.country);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <FactCard label="Address" icon={MapPin}>
        {addressLines.length === 0 ? (
          <span className="text-graphite-faint">—</span>
        ) : (
          <div className="space-y-0.5 leading-relaxed">
            {addressLines.map((l) => (
              <div key={l}>{l}</div>
            ))}
          </div>
        )}
      </FactCard>
      <FactCard label="Email" icon={Mail}>
        {client.email ? (
          <a
            href={`mailto:${client.email}`}
            className="font-mono text-graphite hover:text-ink hover:underline"
          >
            {client.email}
          </a>
        ) : (
          <span className="text-graphite-faint">—</span>
        )}
      </FactCard>
      <FactCard label="Phone" icon={Phone}>
        {client.phone ? (
          <a
            href={`tel:${client.phone.replace(/[^\d+]/g, "")}`}
            className="font-mono text-graphite hover:text-ink hover:underline"
          >
            {client.phone}
          </a>
        ) : (
          <span className="text-graphite-faint">—</span>
        )}
      </FactCard>
    </div>
  );
}

function FactCard({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon: typeof MapPin;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-rule bg-paper-warm px-4 py-3">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-graphite-soft" aria-hidden />
        <span className="label-mono">{label}</span>
      </div>
      <div className="mt-2 text-sm text-graphite">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------
// ClientSummaryCard — quote rollup, lifetime value, last contact.
// Renders nothing until the summary query resolves; the parent
// gates the mount on `summaryQuery.data` being defined.
// ---------------------------------------------------------------

function ClientSummaryCard({ summary }: { summary: ClientSummaryResponse }) {
  const lastContact = summary.last_contact_at ? formatRelative(summary.last_contact_at) : null;
  const lastQuote = summary.last_quote_at ? formatRelative(summary.last_quote_at) : null;

  // Status pill chips, only rendering ones with a non-zero count
  // so a brand-new client doesn't show six "0 draft, 0 sent" pills.
  const statusChips = Object.entries(summary.quotes_by_status)
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <section className="rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
          Activity
        </h2>
        {lastContact && (
          <span className="font-mono text-[11px] text-graphite-soft">
            Last contact {lastContact}
          </span>
        )}
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-4">
        <SummaryStat
          icon={Briefcase}
          label="Jobs"
          value={`${summary.active_jobs} active`}
          sub={`${summary.total_jobs} total`}
        />
        <SummaryStat
          icon={TrendingUp}
          label="Pipeline"
          value={formatCentsShort(summary.pipeline_value_cents)}
          sub={`${countByKeys(summary.quotes_by_status, ["draft", "ready", "sent"])} live quotes`}
        />
        <SummaryStat
          icon={Coins}
          label="Accepted (LTV)"
          value={formatCentsShort(summary.accepted_value_cents)}
          sub={`${summary.quotes_by_status.accepted ?? 0} won`}
        />
        <SummaryStat
          icon={CalendarClock}
          label="Last quote"
          value={lastQuote ?? "—"}
          sub={summary.total_quotes ? `${summary.total_quotes} total` : "no quotes yet"}
        />
      </div>

      {statusChips.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="font-mono text-[11px] text-graphite-faint">Status:</span>
          {statusChips.map(([status, count]) => (
            <span
              key={status}
              className="rounded-full border border-rule bg-paper px-2 py-0.5 font-mono text-[11px] text-graphite-soft"
            >
              {count} {status}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

function SummaryStat({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="rounded-md border border-rule bg-paper px-3 py-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-graphite-soft" aria-hidden />
        <span className="label-mono">{label}</span>
      </div>
      <div className="mt-1 text-base font-bold text-graphite">{value}</div>
      <div className="font-mono text-[11px] text-graphite-soft">{sub}</div>
    </div>
  );
}

function formatCentsShort(cents: number): string {
  if (cents <= 0) return "$0";
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 10_000) return `$${(dollars / 1_000).toFixed(0)}K`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(1)}K`;
  return `$${dollars.toFixed(0)}`;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const ms = Date.now() - then;
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}

function countByKeys(counts: Record<string, number>, keys: ReadonlyArray<string>): number {
  return keys.reduce((acc, k) => acc + (counts[k] ?? 0), 0);
}
